// ---------------------------------------------------------------------------
// Date format used across the fee plan and fee payment pages.
//
// Dates are still STORED, compared and sent to the API as ISO ("YYYY-MM-DD")
// everywhere - only what the person TYPES and READS changes. Everything that
// shows or takes a date on those pages goes through this file, so the format
// is decided in exactly one place.
//
// YEAR_DIGITS = 2  ->  dd-mm-yy    (30-09-26)
// YEAR_DIGITS = 4  ->  dd-mm-yyyy  (30-09-2026)
// ---------------------------------------------------------------------------
export const YEAR_DIGITS = 2;

export const DATE_FNS_DATE_FORMAT = YEAR_DIGITS === 4 ? "dd-MM-yyyy" : "dd-MM-yy";
export const DATE_FNS_DATETIME_FORMAT = `${DATE_FNS_DATE_FORMAT} HH:mm`;
export const DATE_PLACEHOLDER = YEAR_DIGITS === 4 ? "dd-mm-yyyy" : "dd-mm-yy";
export const DATETIME_PLACEHOLDER = `${DATE_PLACEHOLDER} hh:mm`;

const pad2 = (n) => String(n).padStart(2, "0");

// "2026-09-30", "2026-09-30 16:01", "2026-09-30T16:01:00" (no time zone)
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
// ... with a time zone designator ("...Z" / "...+05:30") - the calendar day
// depends on the viewer's zone, so these go through Date, as the dashboards
// did before.
const ISO_WITH_ZONE_RE =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

const formatParts = (y, m, d) => {
  const year = String(y).padStart(4, "0");
  return `${pad2(d)}-${pad2(m)}-${YEAR_DIGITS === 4 ? year : year.slice(-2)}`;
};

// Anything the API / state might hold -> a local Date, or null.
const toLocalDate = (value) => {
  if (value instanceof Date) return isNaN(value) ? null : value;
  const str = String(value).trim();
  if (!ISO_WITH_ZONE_RE.test(str) && ISO_RE.test(str)) return null; // handled by callers as plain text
  const parsed = new Date(str);
  return isNaN(parsed) ? null : parsed;
};

/** "2026-09-30" | "21 Sep 2026" | Date  ->  "30-09-26". Unknown shapes are returned as-is. */
export const formatDisplayDate = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (!(value instanceof Date)) {
    const str = String(value).trim();
    if (!ISO_WITH_ZONE_RE.test(str)) {
      const m = ISO_RE.exec(str);
      if (m) return formatParts(m[1], m[2], m[3]);
    }
  }
  const d = toLocalDate(value);
  if (d) return formatParts(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return String(value);
};

/** "2026-09-30 16:01" -> "30-09-26 16:01" (date only if there is no time part). */
export const formatDisplayDateTime = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (!(value instanceof Date)) {
    const str = String(value).trim();
    if (!ISO_WITH_ZONE_RE.test(str)) {
      const m = ISO_RE.exec(str);
      if (m) {
        const date = formatParts(m[1], m[2], m[3]);
        return m[4] ? `${date} ${m[4]}:${m[5]}` : date;
      }
    }
  }
  const d = toLocalDate(value);
  if (d) {
    return `${formatParts(d.getFullYear(), d.getMonth() + 1, d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  return String(value);
};

/** ISO "YYYY-MM-DD" / "YYYY-MM-DDTHH:mm" -> local Date (no UTC shift), or null. */
export const isoToDate = (value) => {
  if (!value) return null;
  const m = ISO_RE.exec(String(value));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0));
  return isNaN(d) ? null : d;
};

/** Date -> "YYYY-MM-DD" (local day). */
export const dateToIso = (d) =>
  `${String(d.getFullYear()).padStart(4, "0")}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Date -> "YYYY-MM-DDTHH:mm" (same shape a datetime-local input used). */
export const dateToIsoDateTime = (d) => `${dateToIso(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
